import React from "react";
import { Users, Video, ChevronRight, Sparkles, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { sceneConfig, SceneType, CountryLiveStats } from "@/hooks/useScenes";
import { cn } from "@/lib/utils";

interface GlobalConnectCountryCardProps {
  country: {
    code: string;
    name: string;
    flag: string;
  };
  stats?: CountryLiveStats;
  onSelect: () => void;
  onDropIn: () => void;
  featured?: boolean;
}

const GlobalConnectCountryCard: React.FC<GlobalConnectCountryCardProps> = ({
  country,
  stats,
  onSelect,
  onDropIn,
  featured = false,
}) => {
  const liveCount = stats?.liveCount || 0;
  const onVideoCount = stats?.onVideoCount || 0;
  const trendingScene = stats?.trendingScene || "beach";
  const sceneInfo = sceneConfig[trendingScene];
  const isActive = liveCount > 0;

  // Get top scenes for preview (showing first 3)
  const previewScenes = Object.values(sceneConfig)
    .slice(0, 3)
    .map(s => s.emoji + " " + s.label)
    .join(" • ");

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border transition-all duration-300",
        featured ? "bg-card" : "bg-card/80",
        isActive && "border-primary/30 shadow-lg",
        !isActive && "border-border"
      )}
    >
      {/* Trending Scene Gradient Top Bar */}
      {isActive && (
        <div
          className={cn(
            "absolute top-0 left-0 right-0 h-1 bg-gradient-to-r",
            sceneInfo.gradient
          )}
        />
      )}

      {/* Main Interactive Card - NOW CLEARLY CLICKABLE */}
      <button
        onClick={onSelect}
        className={cn(
          "w-full p-4 text-left transition-all duration-200 group cursor-pointer",
          "hover:bg-accent/10 active:bg-accent/20",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        )}
        role="button"
        aria-label={`Choose city and scene for ${country.name}`}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        {/* Top Section: Flag + Country Name + Stats */}
        <div className="flex items-center gap-3 mb-3">
          {/* Flag */}
          <span className={cn("text-3xl", featured && "text-4xl")}>
            {country.flag}
          </span>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className={cn("font-semibold truncate", featured && "text-lg")}>
              {country.name}
            </p>

            {/* Live Stats */}
            <div className="flex items-center gap-3 mt-1 text-sm">
              {/* Live Count */}
              <div className={cn(
                "flex items-center gap-1",
                isActive ? "text-green-600" : "text-muted-foreground"
              )}>
                {isActive && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                  </span>
                )}
                <Users className="w-3.5 h-3.5" />
                <span className="font-medium">{liveCount} Live</span>
              </div>

              {/* On Video Count */}
              {onVideoCount > 0 && (
                <div className="flex items-center gap-1 text-primary">
                  <Video className="w-3.5 h-3.5" />
                  <span className="font-medium">{onVideoCount} On video</span>
                </div>
              )}
            </div>

            {/* Trending Scene */}
            {isActive && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <Sparkles className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-muted-foreground">
                  Trending: {sceneInfo.emoji} {sceneInfo.label}
                </span>
              </div>
            )}
          </div>

          <ChevronRight className={cn(
            "w-5 h-5 text-muted-foreground transition-transform",
            "group-hover:text-primary group-hover:translate-x-1"
          )} />
        </div>

        {/* NEW: Clear Call-to-Action Section */}
        <div className={cn(
          "p-3 rounded-lg border border-dashed transition-all",
          "border-border/50 bg-accent/5",
          "group-hover:border-primary/40 group-hover:bg-primary/5"
        )}>
          {/* Primary Label */}
          <div className="flex items-center gap-2 mb-1">
            <MapPin className={cn(
              "w-4 h-4 transition-colors",
              "text-muted-foreground group-hover:text-primary"
            )} />
            <p className={cn(
              "text-sm font-semibold transition-colors",
              "text-foreground group-hover:text-primary"
            )}>
              Choose city & scene
            </p>
          </div>

          {/* Helper Text */}
          <p className="text-xs text-muted-foreground mb-2">
            Tap anywhere to drop into a vibe
          </p>

          {/* Scene Preview Chips */}
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span className="font-medium">Scenes:</span>
            <span className="truncate">{previewScenes}</span>
          </div>
        </div>
      </button>

      {/* Secondary CTA Button - Only shown for active countries */}
      {isActive && (
        <div className="px-4 pb-4">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onDropIn();
            }}
            className={cn(
              "w-full gap-2 bg-gradient-to-r text-white shadow-lg",
              sceneInfo.gradient,
              "hover:shadow-xl transition-all"
            )}
          >
            {sceneInfo.emoji} Drop into {sceneInfo.label}
          </Button>
        </div>
      )}

      {/* Not Active State - Show Explore Button */}
      {!isActive && (
        <div className="px-4 pb-4">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            className={cn(
              "w-full gap-2",
              "hover:bg-primary/5 hover:border-primary/50 transition-all"
            )}
          >
            {sceneConfig.beach.emoji} Explore Scenes
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default GlobalConnectCountryCard;
